/**
 * audioUnlock
 *
 * Everything iOS needs before Timo can make a sound. Three separate problems,
 * all of which produced the same symptom on a real iPhone: captions render,
 * speakers stay silent.
 *
 * 1. AUTOPLAY GESTURE. Safari (and standalone PWA) only lets audio start after
 *    a real user gesture, and the permission is per-object: an AudioContext must
 *    be resumed inside the tap, and an HTMLAudioElement must have had .play()
 *    called on IT inside the tap. Both are blessed here, synchronously.
 *
 * 2. THE HARDWARE RING/SILENT SWITCH. This is the one the previous fix missed.
 *    On iOS an HTMLAudioElement plays into the "ambient" audio session, which
 *    the physical mute switch silences outright. play() still resolves, no error
 *    is thrown, timeupdate still fires: from JavaScript the playback looks
 *    perfect while the phone emits nothing. That exactly matches "captions work,
 *    no sound, no errors". Two mitigations, both applied:
 *      a. Play through WebAudio (AudioBufferSourceNode) as the primary path.
 *      b. Keep a silent looping <audio> running, which nudges the page's audio
 *         session out of "ambient" so WebAudio output survives the mute switch.
 *
 * 3. SUSPENSION. iOS aggressively suspends the AudioContext on backgrounding,
 *    on PWA resume, and sometimes right after the mic stream opens. Every play
 *    therefore re-resumes the context first, and visibilitychange re-blesses.
 *
 * `unlockAudio()` is idempotent, never throws, and MUST be called synchronously
 * inside the tap handler. Everything else here is safe to call at any time.
 */

// Only used by isStandalone() below. `platform.js` has zero imports of its own.
// Note isIOS() a few lines down is a BROWSER iOS check and answers a different
// question: it must not be re-pointed at the native check.
import { isNative } from '@/lib/platform';

let ctx = null;
let unlocked = false;
let sharedAudioEl = null;
let elementBlessed = false;
let sessionLoopEl = null;
let visibilityHooked = false;

/* ------------------------------------------------------------------ */
/* environment                                                        */
/* ------------------------------------------------------------------ */

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as desktop Safari; touch points give it away.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  // The native shell is a standalone surface too, and every display-mode test
  // is false inside a Capacitor webview. isNative() is a constant false in
  // every browser, so the three lines below are unchanged on web.
  if (isNative()) return true;
  if (window.navigator && window.navigator.standalone === true) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/** Compact snapshot for diagnostics / analytics. */
export function audioEnv() {
  return {
    isIOS: isIOS(),
    isStandalone: isStandalone(),
    ctxState: ctx ? ctx.state : 'none',
    elementBlessed,
    unlocked,
  };
}

export function isUnlocked() {
  return unlocked;
}

export function isElementBlessed() {
  return elementBlessed;
}

/**
 * The single persistent HTMLAudioElement used for element-path playback. May be
 * null before the first unlock; callers fall back to a fresh Audio().
 */
export function getSharedAudio() {
  return sharedAudioEl;
}

export function getAudioContext() {
  return ctx;
}

/* ------------------------- iOS output routing ---------------------------- *
 * The single worst thing an iPhone does to this feature.
 *
 * The moment a page opens the microphone, iOS moves the audio session into the
 * play-and-record category, and the DEFAULT OUTPUT ROUTE for that category is
 * the receiver, the little earpiece you hold to your head on a call. So Timo's
 * first line plays loud through the speaker (no mic has been opened yet) and
 * every line after the child's first turn comes out of the earpiece at a
 * fraction of the volume. Nothing is broken, nothing errors, and a parent
 * testing the app just concludes it does not work.
 *
 * Releasing the mic tracks does not reliably hand the route back. The fix is to
 * declare the session type explicitly. Safari 16.4+ exposes navigator.audioSession:
 * 'playback' means "I am only playing audio", which routes to the loudspeaker,
 * and 'play-and-record' is set only while actually recording.
 *
 * Everywhere that lacks the API (Android, desktop, older iOS) this is a no-op,
 * and those platforms do not have the routing problem in the first place.
 * ------------------------------------------------------------------------- */
function setAudioSession(type) {
  try {
    if (typeof navigator === 'undefined') return false;
    const s = navigator.audioSession;
    if (!s) return false;
    s.type = type;
    return true;
  } catch {
    // Assigning an unsupported value throws on some builds; never fatal.
    return false;
  }
}

// True between opening the mic and releasing it. The TTS player calls
// routeAudioToSpeaker() on every playback and has no idea whether a recording
// is in flight, so this flag stops it from pulling the session out of
// play-and-record underneath a live MediaRecorder and killing the child's turn.
let recordingRoute = false;

/**
 * Call immediately before playing TTS: forces output to the loudspeaker.
 * Deliberately does nothing while the mic is open, since recording needs the
 * play-and-record category and cutting it short matters more than the route.
 */
export function routeAudioToSpeaker() {
  if (recordingRoute) return false;
  return setAudioSession('playback');
}

/** Call while the microphone is open. Recording requires this category. */
export function routeAudioForRecording() {
  recordingRoute = true;
  return setAudioSession('play-and-record');
}

/**
 * Call once the mic tracks are stopped: drops the recording claim and takes the
 * loudspeaker back, which is the whole point of this module.
 */
export function releaseRecordingRoute() {
  recordingRoute = false;
  return setAudioSession('playback');
}

/** True when this browser lets us control the route at all (iOS 16.4+). */
export function canRouteAudio() {
  return typeof navigator !== 'undefined' && !!navigator.audioSession;
}

/* ------------------------------------------------------------------ */
/* silent WAV                                                         */
/* ------------------------------------------------------------------ */

/**
 * Build a valid silent WAV data URI deterministically (no hand-typed base64 to
 * get wrong). 16-bit mono PCM, so "all zero bytes" is unambiguously silence
 * (8-bit WAV is unsigned, where silence would be 0x80, an easy trap).
 *
 * `seconds > 0` is required for the looping session element: a zero-length data
 * chunk cannot loop, and iOS will not hold the audio session open for it.
 */
function silentWavUri(seconds = 0) {
  const sampleRate = 8000;
  const frames = Math.max(0, Math.round(sampleRate * seconds));
  const dataBytes = frames * 2; // 16-bit mono
  const bytes = new Uint8Array(44 + dataBytes);
  const dv = new DataView(bytes.buffer);
  const wr = (off, str) => {
    for (let i = 0; i < str.length; i += 1) dv.setUint8(off + i, str.charCodeAt(i));
  };
  wr(0, 'RIFF');
  dv.setUint32(4, 36 + dataBytes, true);
  wr(8, 'WAVE');
  wr(12, 'fmt ');
  dv.setUint32(16, 16, true); // PCM fmt chunk size
  dv.setUint16(20, 1, true); // PCM
  dv.setUint16(22, 1, true); // mono
  dv.setUint32(24, sampleRate, true);
  dv.setUint32(28, sampleRate * 2, true); // byte rate
  dv.setUint16(32, 2, true); // block align
  dv.setUint16(34, 16, true); // bits per sample
  wr(36, 'data');
  dv.setUint32(40, dataBytes, true);
  // Sample bytes are already zero == silence.
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(bin)}`;
}

/* ------------------------------------------------------------------ */
/* audio session (ring/silent switch mitigation)                      */
/* ------------------------------------------------------------------ */

/**
 * A silent, looping, inline audio element kept alive for the whole talk session.
 * On iOS this keeps the page's audio session active and out of the pure
 * "ambient" category, which is what lets WebAudio output survive the hardware
 * mute switch. It is genuinely silent, so it costs the user nothing.
 *
 * Not gated to iOS only by feature detection (there is none for this); gated by
 * isIOS() so no other platform pays for a permanently playing element.
 */
function startSilentSession() {
  if (!isIOS()) return;
  try {
    if (typeof Audio === 'undefined') return;
    if (!sessionLoopEl) {
      sessionLoopEl = new Audio(silentWavUri(0.25));
      sessionLoopEl.loop = true;
      sessionLoopEl.preload = 'auto';
      sessionLoopEl.setAttribute('playsinline', '');
      sessionLoopEl.setAttribute('webkit-playsinline', '');
      // Deliberately NOT muted: a muted element does not hold the session.
      sessionLoopEl.volume = 1;
    }
    if (sessionLoopEl.paused) {
      const p = sessionLoopEl.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* blocked, non-fatal */ });
    }
  } catch {
    /* non-fatal */
  }
}

export function stopSilentSession() {
  try {
    if (sessionLoopEl && !sessionLoopEl.paused) sessionLoopEl.pause();
  } catch {
    /* non-fatal */
  }
}

/* ------------------------------------------------------------------ */
/* blessing                                                           */
/* ------------------------------------------------------------------ */

function blessAudioElement() {
  try {
    if (typeof Audio === 'undefined') return;
    if (!sharedAudioEl) {
      sharedAudioEl = new Audio();
      sharedAudioEl.preload = 'auto';
      // playsinline matters in standalone PWA, where a bare audio element can
      // otherwise be handed to the native full-screen player.
      sharedAudioEl.setAttribute('playsinline', '');
      sharedAudioEl.setAttribute('webkit-playsinline', '');
      sharedAudioEl.volume = 1;
    }
    if (elementBlessed) return;
    // Give it a real (silent) source and start playback INSIDE the gesture.
    // After this, later programmatic .play() with a blob src is permitted.
    sharedAudioEl.src = silentWavUri(0.05);
    const p = sharedAudioEl.play();
    if (p && typeof p.then === 'function') {
      p
        .then(() => {
          elementBlessed = true;
          try {
            sharedAudioEl.pause();
            sharedAudioEl.currentTime = 0;
          } catch { /* non-fatal */ }
        })
        .catch(() => {
          // Autoplay still blocked (rare after a genuine tap). Leave unblessed
          // so the next tap retries; playback falls back to fresh Audio().
        });
    } else {
      // Legacy browsers where play() returns undefined: assume blessed.
      elementBlessed = true;
    }
  } catch {
    /* non-fatal */
  }
}

function hookVisibility() {
  if (visibilityHooked || typeof document === 'undefined') return;
  visibilityHooked = true;
  const rebless = () => {
    if (!unlocked) return;
    if (document.visibilityState !== 'visible') return;
    // iOS suspends the context on background and on PWA resume. Resuming here
    // is allowed because the context was already unlocked by a real gesture.
    try {
      if (ctx && ctx.state === 'suspended') {
        const p = ctx.resume();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    } catch { /* non-fatal */ }
    startSilentSession();
  };
  try {
    document.addEventListener('visibilitychange', rebless);
    window.addEventListener('pageshow', rebless);
    window.addEventListener('focus', rebless);
  } catch {
    /* non-fatal */
  }
}

export function unlockAudio() {
  // (a) HTMLAudioElement domain: fallback playback path.
  blessAudioElement();
  // (b) Audio session: keeps WebAudio audible with the ring switch on iOS.
  startSilentSession();
  hookVisibility();

  // (c) WebAudio domain: the PRIMARY playback path.
  try {
    const Ctx = typeof window !== 'undefined'
      ? (window.AudioContext || window.webkitAudioContext)
      : null;
    if (!Ctx) return null;

    if (!ctx) {
      ctx = new Ctx();
    }

    // Resume is async but must be *called* inside the gesture.
    if (ctx.state === 'suspended') {
      const p = ctx.resume();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    // A 1-sample silent buffer satisfies iOS's "audio was started by a gesture" check.
    try {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      if (typeof source.start === 'function') source.start(0);
      else if (typeof source.noteOn === 'function') source.noteOn(0);
    } catch {
      // Buffer play failed; the resume() above is still worth keeping.
    }

    unlocked = true;
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Await a running AudioContext, resuming it if iOS suspended it since the last
 * play. Returns null when WebAudio is unavailable, and returns the context even
 * if it is still not 'running' so the caller can report the exact state.
 */
export async function ensureAudioContextRunning() {
  if (!ctx) {
    // Never been unlocked (e.g. speakLine before any tap). Try to create one:
    // harmless, and on desktop it will be 'running' immediately.
    try {
      const Ctx = typeof window !== 'undefined'
        ? (window.AudioContext || window.webkitAudioContext)
        : null;
      if (!Ctx) return null;
      ctx = new Ctx();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
    try {
      await ctx.resume();
    } catch {
      /* report the state to the caller instead of throwing */
    }
  }
  return ctx;
}

export default unlockAudio;
